// The domino tile model — everything about how ONE tile looks lives here, so
// it can be swapped for a Blender GLB later without touching the game views.
//
// Procedural, light for Quest:
//  - one shared material whose map is a small canvas atlas (8 cells: blank
//    ivory + the faces 0–6), so every tile on the table uses the same
//    texture and material;
//  - a cached geometry per face pair (rounded ivory body + two face quads
//    UV-mapped into the atlas), and
//  - <DominoGroup> merges any number of tiles into ONE mesh (one draw call),
//    rebuilt only when the tiles change (a move), never per frame.
//
// Tile frame: long axis +Z, face up (+Y), sitting on y = 0. Pip `a` is on the
// −Z half, `b` on the +Z half. `b === null` / `a === null` = a face-down /
// hidden tile (plain ivory top).
//
// To swap in a GLB: replace `tileGeometry()` with one that returns the GLB's
// geometry (baked to this frame and TILE size, UVs into the same atlas or
// your own material from `dominoMaterial()`); the views only call
// tileGeometry / dominoMaterial / DominoGroup / dominoFaceTexture.

import React, { useEffect, useMemo } from 'react';
import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { PIP_LAYOUT, PIP_R } from './pips';
import { noRaycast } from '../../VR/vrUi';

/** Tile size (m): length, width, thickness. Matches layout.ts DEFAULT_LAYOUT. */
export const TILE = { L: 0.056, W: 0.028, H: 0.011 };

const IVORY = '#f4efe2';
const PIP = '#15171b';
const CELLS = 8;       // 0 = blank, 1..7 = faces 0..6
const CELL_PX = 128;

/** Draw one half-face (pips `n`) into a square at (x, y) of side s. `divider`: which edge gets the centre line. */
export function drawHalf(g: CanvasRenderingContext2D, n: number, x: number, y: number, s: number, divider: 'top' | 'bottom' | 'none' = 'top') {
  g.fillStyle = IVORY;
  g.fillRect(x, y, s, s);
  g.fillStyle = PIP;
  for (const [px, py] of PIP_LAYOUT[n] ?? []) {
    g.beginPath();
    g.arc(x + px * s, y + py * s, PIP_R * s, 0, Math.PI * 2);
    g.fill();
  }
  if (divider !== 'none') {
    g.fillStyle = '#5c5648';
    const t = Math.max(1, s * 0.022);
    g.fillRect(x + s * 0.12, divider === 'top' ? y : y + s - t, s * 0.76, t);
  }
}

let atlas: THREE.CanvasTexture | null = null;
function atlasTexture(): THREE.CanvasTexture {
  if (atlas) return atlas;
  const c = document.createElement('canvas');
  c.width = CELLS * CELL_PX; c.height = CELL_PX;
  const g = c.getContext('2d')!;
  g.fillStyle = IVORY; g.fillRect(0, 0, c.width, c.height);
  for (let n = 0; n <= 6; n++) drawHalf(g, n, (n + 1) * CELL_PX, 0, CELL_PX, 'top');
  atlas = new THREE.CanvasTexture(c);
  atlas.colorSpace = THREE.SRGBColorSpace;
  atlas.anisotropy = 4;
  return atlas;
}

let material: THREE.MeshStandardMaterial | null = null;
/** The one material every procedural tile shares. */
export function dominoMaterial(): THREE.MeshStandardMaterial {
  if (!material) material = new THREE.MeshStandardMaterial({ map: atlasTexture(), roughness: 0.42, metalness: 0 });
  return material;
}

// u range of atlas cell k (0 = blank, n + 1 = n pips), inset to avoid bleeding.
const cellU = (k: number): [number, number] => [(k + 0.02) / CELLS, (k + 0.98) / CELLS];

let body: THREE.BufferGeometry | null = null;
function bodyGeometry(): THREE.BufferGeometry {
  if (body) return body;
  const rb = new RoundedBoxGeometry(TILE.W, TILE.H, TILE.L, 2, 0.0028);
  const g = rb.index ? rb.toNonIndexed() : rb;
  g.translate(0, TILE.H / 2, 0);
  // Every body vertex samples the blank ivory cell.
  const uv = g.getAttribute('uv') as THREE.BufferAttribute;
  for (let i = 0; i < uv.count; i++) uv.setXY(i, 0.5 / CELLS, 0.5);
  body = g;
  return g;
}

/** A face quad for one half centred at z = zCentre, UVs into the atlas cell for `n` pips. */
function halfQuad(n: number, zCentre: number, flip: boolean): THREE.BufferGeometry {
  const s = TILE.W * 0.9;
  const q = new THREE.PlaneGeometry(s, s).toNonIndexed();
  // Both halves meet at the tile's centre line, so their divider strokes read as one line.
  q.rotateX(-Math.PI / 2);                      // face up; plane +V now points −Z
  if (flip) q.rotateY(Math.PI);                 // divider must face the tile centre
  q.translate(0, TILE.H + 0.0004, zCentre);
  const [u0, u1] = cellU(n + 1);
  const uv = q.getAttribute('uv') as THREE.BufferAttribute;
  for (let i = 0; i < uv.count; i++) uv.setX(i, u0 + uv.getX(i) * (u1 - u0));
  return q;
}

const geoCache = new Map<string, THREE.BufferGeometry>();
/**
 * Geometry of one tile in the tile frame. a/b = pips on the −Z / +Z half;
 * pass null for a face-down tile.
 */
export function tileGeometry(a: number | null, b: number | null): THREE.BufferGeometry {
  const key = a === null || b === null ? 'blank' : `${a}${b}`;
  const hit = geoCache.get(key);
  if (hit) return hit;
  const parts = [bodyGeometry()];
  if (a !== null && b !== null) {
    // Atlas cells draw the divider on their top edge (v = 1). After rotateX the
    // top edge points −Z, so the +Z half is already right and the −Z half flips.
    parts.push(halfQuad(a, -TILE.W * 0.45, true), halfQuad(b, TILE.W * 0.45, false));
  }
  const g = parts.length === 1 ? parts[0] : mergeGeometries(parts)!;
  geoCache.set(key, g);
  return g;
}

/** A tile placed in a DominoGroup: pips (null = hidden) + its transform in the group. */
export interface TilePlacement {
  a: number | null; b: number | null;
  position: [number, number, number];
  /** Euler angles (radians), applied in `order` (default 'XYZ'). */
  rotation: [number, number, number];
  order?: THREE.EulerOrder;
}

const _m = new THREE.Matrix4();
const _q = new THREE.Quaternion();
const _e = new THREE.Euler();
const _s = new THREE.Vector3(1, 1, 1);
const _p = new THREE.Vector3();

/** Many tiles as one merged mesh (one draw call). Rebuilt when `tiles` changes. */
export function DominoGroup({ tiles, castShadow = true }: { tiles: TilePlacement[]; castShadow?: boolean }) {
  const geometry = useMemo(() => {
    if (!tiles.length) return null;
    const parts = tiles.map((t) => {
      const g = tileGeometry(t.a, t.b).clone();
      _m.compose(_p.set(...t.position), _q.setFromEuler(_e.set(t.rotation[0], t.rotation[1], t.rotation[2], t.order ?? 'XYZ')), _s);
      g.applyMatrix4(_m);
      return g;
    });
    const merged = mergeGeometries(parts);
    for (const p of parts) p.dispose();
    return merged;
  }, [tiles]);
  useEffect(() => () => geometry?.dispose(), [geometry]);
  if (!geometry) return null;
  return (
    <mesh geometry={geometry} material={dominoMaterial()} castShadow={castShadow} receiveShadow
      raycast={noRaycast as any} userData={{ collidable: false }} />
  );
}

/** A single tile (e.g. for a swap-in preview). Same look as DominoGroup. */
export function DominoTile({ a, b, position, rotation, order }: TilePlacement) {
  return (
    <mesh geometry={tileGeometry(a, b)} material={dominoMaterial()} position={position} rotation={new THREE.Euler(...rotation, order ?? 'XYZ')}
      castShadow receiveShadow raycast={noRaycast as any} userData={{ collidable: false }} />
  );
}

const faceCache = new Map<string, THREE.CanvasTexture>();
/** Upright face picture of a tile (a on top), for VR buttons. Cached per tile. */
export function dominoFaceTexture(tile: string): THREE.CanvasTexture {
  const hit = faceCache.get(tile);
  if (hit) return hit;
  const s = 96, pad = 6;
  const c = document.createElement('canvas');
  c.width = s + pad * 2; c.height = s * 2 + pad * 2;
  const g = c.getContext('2d')!;
  g.fillStyle = '#c9c1ad'; g.fillRect(0, 0, c.width, c.height);
  drawHalf(g, Number(tile[0]), pad, pad, s, 'bottom');
  drawHalf(g, Number(tile[2]), pad, pad + s, s, 'none');
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  faceCache.set(tile, tex);
  return tex;
}
